import { useMemo } from 'react';
import { useEmotionData } from './EmotionData';
import { useCameraData } from '../hooks/useCameraData';

const BODY_LANGUAGE_KEYS = [
  'body_language',
  'bodyLanguage',
  'BodyLanguage',
  'body_posture',
  'bodyPosture',
  'BodyPosture',
  'posture',
  'Posture',
  'body_language_primary',
  'bodyLanguagePrimary'
];

const CONFIDENCE_KEYS = [
  'body_language_confidence',
  'bodyLanguageConfidence',
  'BodyLanguageConfidence',
  'body_language_score',
  'bodyLanguageScore'
];

const DURATION_KEYS = [
  'session_duration',
  'Session_Duration',
  'sessionDuration',
  'duration',
  'Duration'
];

const START_TIME_KEYS = [
  'session_start_time',
  'Session_Start_Time',
  'sessionStartTime',
  'start_time',
  'startTime'
];

const normalizeLabel = (value) => {
  if (!value) {
    return null;
  }

  const stringValue = value
    .toString()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/_/g, ' ')
    .toLowerCase();

  if (!stringValue) {
    return null;
  }

  return stringValue
    .split(' ')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const getFirstAvailableValue = (obj, keys) => {
  for (const key of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return null;
};

const tryParseFloat = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const tryParseDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

export const useBodyLanguageData = (artworkId, minDuration = 3.0) => {
  const { rawData, loading, error } = useCameraData(artworkId, 'body-language');
  console.log("rawData", rawData);
  const processedData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return {
        distribution: [],
        averageDuration: [],
        timeline: [],
        confidence: []
      };
    }

    const aggregates = {};

    rawData.forEach((session, index) => {
      const rawBodyLanguage = getFirstAvailableValue(session, BODY_LANGUAGE_KEYS);

      if (!rawBodyLanguage) {
        return;
      }

      // Support arrays by iterating over each value
      const values = Array.isArray(rawBodyLanguage) ? rawBodyLanguage : [rawBodyLanguage];

      values
        .map((value) => normalizeLabel(value))
        .filter(Boolean)
        .forEach((label) => {
          if (!aggregates[label]) {
            aggregates[label] = {
              label,
              count: 0,
              totalDuration: 0,
              totalConfidence: 0,
              confidenceCount: 0,
              timeline: []
            };
          }

          aggregates[label].count += 1;

          const durationValue = getFirstAvailableValue(session, DURATION_KEYS);
          const parsedDuration = tryParseFloat(durationValue);

          if (parsedDuration !== null) {
            aggregates[label].totalDuration += parsedDuration;
          }

          const confidenceValue = getFirstAvailableValue(session, CONFIDENCE_KEYS);
          const parsedConfidence = tryParseFloat(confidenceValue);

          if (parsedConfidence !== null) {
            aggregates[label].totalConfidence += parsedConfidence;
            aggregates[label].confidenceCount += 1;
          }

          const startTimeValue = getFirstAvailableValue(session, START_TIME_KEYS);
          const parsedStartTime = tryParseDate(startTimeValue);

          aggregates[label].timeline.push({
            order: parsedStartTime ? parsedStartTime.getTime() : index,
            duration: parsedDuration,
            confidence: parsedConfidence
          });
        });
    });

    const distribution = Object.values(aggregates)
      .map(({ label, count }) => ({
        label,
        count
      }))
      .sort((a, b) => b.count - a.count);

    const averageDuration = Object.values(aggregates)
      .map(({ label, count, totalDuration }) => ({
        label,
        averageDuration: count > 0 ? totalDuration / count : 0
      }))
      .filter((item) => item.averageDuration > 0)
      .sort((a, b) => b.averageDuration - a.averageDuration);

    const confidence = Object.values(aggregates)
      .map(({ label, totalConfidence, confidenceCount }) => ({
        label,
        averageConfidence: confidenceCount > 0 ? totalConfidence / confidenceCount : null
      }))
      .filter((item) => item.averageConfidence !== null)
      .sort((a, b) => b.averageConfidence - a.averageConfidence);

    const timeline = Object.values(aggregates).map(({ label, timeline: samples }) => {
      const sortedSamples = samples
        .filter((sample) => sample.duration !== null || sample.confidence !== null)
        .sort((a, b) => a.order - b.order);

      return {
        label,
        samples: sortedSamples
      };
    });

    return {
      distribution,
      averageDuration,
      timeline,
      confidence
    };
  }, [rawData]);

  return {
    rawData,
    loading,
    error,
    processedData
  };
};

export default useBodyLanguageData;


